
import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, Flame, Skull, Heart, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase.ts';
import { trackEvent } from '../lib/analytics.ts';
import { UIImage, AppView, AuthView } from '../types.ts';

// Updated to the specific high-aura asset requested (ID 866)
const LIT_SOUND_URL = 'https://assets.mixkit.co/active_storage/sfx/866/866-preview.mp3';

interface VibeCheckViewProps {
  user: any;
  isGuest: boolean;
  addAura: (amount: number) => Promise<number>;
  notify: (msg: string, type?: any) => void;
  setActiveView: (view: AppView) => void;
  setAuthView: (view: AuthView) => void;
}

export default function VibeCheckView({ user, isGuest, addAura, notify, setActiveView, setAuthView }: VibeCheckViewProps) {
  const [feed, setFeed] = useState<UIImage[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [vibeFeedback, setVibeFeedback] = useState<{ id: string; text: string; type: 'lit' | 'mid' | 'aura' }[]>([]);
  const [isAudioUnlocked, setIsAudioUnlocked] = useState(false);
  
  // Ref for a shared AudioContext to handle procedural sounds (Mid)
  const audioCtxRef = useRef<AudioContext | null>(null);
  // Ref for the Lit MP3
  const litSoundRef = useRef<HTMLAudioElement | null>(null);

  // Initialize Audio
  useEffect(() => {
    const AudioContextClass = (window.AudioContext || (window as any).webkitAudioContext);
    audioCtxRef.current = new AudioContextClass();
    
    const audio = new Audio();
    audio.src = LIT_SOUND_URL;
    audio.crossOrigin = "anonymous";
    audio.volume = 0.5;
    litSoundRef.current = audio;

    return () => {
      if (audioCtxRef.current) audioCtxRef.current.close();
      if (litSoundRef.current) {
        litSoundRef.current.pause();
        litSoundRef.current = null;
      }
    };
  }, []);

  /**
   * Bypasses browser auto-play restrictions (especially on mobile)
   */
  const unlockAudio = async () => {
    if (isAudioUnlocked) return;
    
    // Resume context
    if (audioCtxRef.current && audioCtxRef.current.state === 'suspended') {
      await audioCtxRef.current.resume();
    }
    
    // Prime the MP3
    if (litSoundRef.current) {
      litSoundRef.current.play().then(() => {
        litSoundRef.current!.pause();
        litSoundRef.current!.currentTime = 0;
      }).catch(() => {});
    }
    
    setIsAudioUnlocked(true);
  };

  useEffect(() => {
    async function fetchVibeFeed() {
      setIsLoading(true);
      try {
        let votedIds: string[] = [];
        if (user && !isGuest) {
          const { data: voted } = await supabase.from('votes').select('meme_id').eq('user_id', user.id);
          votedIds = voted?.map(v => v.meme_id) || [];
        }

        let query = supabase.from('memes').select('*').eq('is_public', true);
        if (user && !isGuest) {
          query = query.neq('user_id', user.id);
          if (votedIds.length > 0) {
            query = query.not('id', 'in', `(${votedIds.join(',')})`);
          }
        }
        
        const { data } = await query.order('timestamp', { ascending: false }).limit(20);
        setFeed(data || []);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    }
    fetchVibeFeed();
  }, [user, isGuest]);

  const playFeedbackSound = async (type: 'lit' | 'mid') => {
    if (type === 'lit') {
      if (litSoundRef.current) {
        litSoundRef.current.currentTime = 0;
        litSoundRef.current.play().catch(() => {});
      }
    } else {
      // Keep "Mid" procedural for that gritty, low-fi thud feel
      const ctx = audioCtxRef.current;
      if (!ctx) return;
      if (ctx.state === 'suspended') await ctx.resume();

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      const now = ctx.currentTime;

      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(150, now);
      osc.frequency.exponentialRampToValueAtTime(40, now + 0.2);
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      osc.start(now);
      osc.stop(now + 0.3);
    }
  };

  const triggerFeedback = (text: string, type: 'lit' | 'mid' | 'aura') => {
    const id = Math.random().toString(36).substring(7);
    setVibeFeedback(prev => [...prev, { id, text, type }]);
    setTimeout(() => {
      setVibeFeedback(prev => prev.filter(f => f.id !== id));
    }, 1000);
  };

  const handleVote = async (type: 'like' | 'dislike') => {
    unlockAudio();
    
    if (isGuest) {
      setAuthView('signup');
      return;
    }

    const currentMeme = feed[currentIndex];
    if (!currentMeme) return;

    playFeedbackSound(type === 'like' ? 'lit' : 'mid');

    try {
      const { error } = await supabase.from('votes').insert({
        user_id: user.id,
        meme_id: currentMeme.id,
        vote_type: type
      });
      
      if (error) throw error;

      triggerFeedback(type === 'like' ? 'LIT!' : 'MID...', type === 'like' ? 'lit' : 'mid');
      const gained = await addAura(1);
      triggerFeedback(`+${gained} AURA`, 'aura');

      trackEvent('vibe_check_vote', {
        vote_type: type,
        meme_id: currentMeme.id,
        rarity: currentMeme.rarity
      });

      setTimeout(() => {
        setCurrentIndex(prev => prev + 1);
      }, 300);
    } catch (e: any) {
      if (e.code === '23505') {
        notify("ALREADY VOTED ON THIS ROT", "info");
        setCurrentIndex(prev => prev + 1);
      } else {
        notify("VOTE FAILED", "error");
      }
    }
  };

  const currentMeme = feed[currentIndex];

  return (
    <div className="max-w-4xl mx-auto space-y-8 animate-in slide-in-from-bottom-8">
      <div className="flex items-center gap-4">
        <button onClick={() => setActiveView('editor')} className="p-3 bg-zinc-900 rounded-2xl shadow-lg hover:scale-110 active:scale-90 transition-transform">
          <ChevronLeft size={24}/>
        </button>
        <h2 className="text-5xl font-bungee text-red-600 uppercase tracking-tighter">VIBE CHECK</h2>
      </div>

      <div className="bg-zinc-900 border-4 border-zinc-800 rounded-[3rem] overflow-hidden shadow-2xl flex flex-col">
        {isLoading ? (
          <div className="aspect-square flex flex-col items-center justify-center gap-4 opacity-40">
            <Loader2 size={48} className="animate-spin text-red-600" />
            <span className="font-bungee uppercase tracking-[0.2em]">Pinging the Matrix...</span>
          </div>
        ) : currentMeme ? (
          <div className="w-full flex flex-col">
            <div className="relative w-full aspect-square bg-zinc-950 flex items-center justify-center overflow-hidden">
              <img src={currentMeme.url} className="w-full h-full object-contain" alt={currentMeme.name} />
              
              <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center gap-4 z-20">
                {vibeFeedback.map(f => (
                  <div key={f.id} className={`font-bungee text-4xl uppercase animate-out fade-out slide-out-to-top-20 duration-1000 drop-shadow-[0_0_15px_rgba(0,0,0,0.8)] ${f.type === 'lit' ? 'text-orange-500' : f.type === 'mid' ? 'text-zinc-500' : 'text-yellow-400'}`}>
                    {f.text}
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 bg-zinc-950 border-t-4 border-zinc-800 space-y-3">
              <h3 className="text-4xl font-bungee text-yellow-400 uppercase leading-none">{currentMeme.name}</h3>
              <p className="text-zinc-100 font-bold italic leading-relaxed text-lg">"{currentMeme.story}"</p>
            </div>
          </div>
        ) : (
          <div className="aspect-square flex flex-col items-center justify-center p-20 space-y-6 opacity-30">
            <Heart size={80} className="mx-auto" />
            <p className="font-bungee text-2xl uppercase text-center">No more rots left to check.<br/>Cook more and refresh!</p>
            <button onClick={() => window.location.reload()} className="px-8 py-4 bg-zinc-800 rounded-2xl font-bungee text-sm">REFRESH FEED</button>
          </div>
        )}
      </div>

      {currentMeme && (
        <div className="grid grid-cols-2 gap-6 h-24">
          <button 
            onClick={() => handleVote('like')}
            className="bg-red-600 border-b-8 border-red-900 rounded-3xl flex items-center justify-center gap-3 font-bungee text-2xl text-white hover:bg-red-500 hover:scale-[1.02] active:translate-y-2 active:border-b-0 transition-all shadow-[0_0_30px_rgba(220,38,38,0.3)]"
          >
            <Flame size={32} /> LIT
          </button>
          <button 
            onClick={() => handleVote('dislike')}
            className="bg-zinc-100 border-b-8 border-zinc-300 rounded-3xl flex items-center justify-center gap-3 font-bungee text-2xl text-zinc-950 hover:bg-white hover:scale-[1.02] active:translate-y-2 active:border-b-0 transition-all shadow-lg"
          >
            <Skull size={32} /> MID
          </button>
        </div>
      )}
    </div>
  );
}
